// Lightweight custom-canvas confetti. No external library — keeps the
// bundle small. Call fireConfetti() to launch a burst from the bottom
// of the viewport; the canvas overlay is created on the fly, animated
// with requestAnimationFrame, and torn down once all particles fade.
//
// fireConfetti({ origin: "top" | "middle" | "bottom" })  controls
// where the burst originates. Default is "middle".
//
// fireMilestone(key) is a higher-level helper — it fires confetti
// once per localStorage key, then sets that key so it never fires
// again for the same user on the same browser. Use it to celebrate
// one-shot moments (first publish, first century, etc.).

const COLORS = [
  "#f7c948", "#ffd770", "#ff8a2a", "#ff3b3b",
  "#5ea8ff", "#b07cff", "#43d893", "#3ed3e0"
];

export function fireConfetti(opts = {}) {
  if (typeof document === "undefined") return;
  if (typeof window === "undefined") return;
  // Respect reduced motion — silently no-op rather than blast effects
  // at someone who's asked for less animation.
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  const canvas = document.createElement("canvas");
  canvas.style.position = "fixed";
  canvas.style.inset = "0";
  canvas.style.pointerEvents = "none";
  canvas.style.zIndex = "9999";
  const dpr = window.devicePixelRatio || 1;
  canvas.width  = window.innerWidth  * dpr;
  canvas.height = window.innerHeight * dpr;
  canvas.style.width  = window.innerWidth  + "px";
  canvas.style.height = window.innerHeight + "px";
  document.body.appendChild(canvas);
  const ctx = canvas.getContext("2d");
  ctx.scale(dpr, dpr);

  const W = window.innerWidth;
  const H = window.innerHeight;

  // Origin Y position based on opts.origin.
  let originY;
  if      (opts.origin === "top")    originY = H * 0.18;
  else if (opts.origin === "bottom") originY = H * 0.92;
  else                               originY = H * 0.45;
  const originX = W / 2;

  // Particles — square and ribbon mix for variety.
  const count = opts.count || 140;
  const particles = [];
  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * (Math.random() - 0.5)) - Math.PI / 2; // upward-ish
    const speed = 6 + Math.random() * 9;
    particles.push({
      x: originX + (Math.random() - 0.5) * 80,
      y: originY,
      vx: Math.cos(angle) * speed + (Math.random() - 0.5) * 3,
      vy: Math.sin(angle) * speed,
      g:  0.18 + Math.random() * 0.06,
      drag: 0.985,
      rot: Math.random() * Math.PI * 2,
      vrot: (Math.random() - 0.5) * 0.3,
      size: 5 + Math.random() * 6,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      shape: Math.random() < 0.4 ? "ribbon" : "square",
      life: 1
    });
  }

  let raf;
  let frame = 0;
  function tick() {
    frame++;
    ctx.clearRect(0, 0, W, H);
    let alive = 0;
    for (const p of particles) {
      if (p.life <= 0) continue;
      // Physics
      p.x += p.vx;
      p.y += p.vy;
      p.vx *= p.drag;
      p.vy = p.vy * p.drag + p.g;
      p.rot += p.vrot;
      // Fade out as they fall off screen or after 180 frames (~3 sec)
      if (p.y > H + 40 || frame > 180) p.life -= 0.04;
      if (p.life > 0) {
        alive++;
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        if (p.shape === "ribbon") {
          ctx.fillRect(-p.size, -p.size * 0.3, p.size * 2, p.size * 0.6);
        } else {
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
        }
        ctx.restore();
      }
    }
    if (alive > 0 && frame < 260) {
      raf = requestAnimationFrame(tick);
    } else {
      cancelAnimationFrame(raf);
      canvas.remove();
    }
  }
  raf = requestAnimationFrame(tick);
}

// Fire confetti once per localStorage key — used for celebrating
// one-shot user achievements.
const KEY_PREFIX = "gabruz-milestone:";
export function fireMilestone(key, opts = {}) {
  if (!key) return false;
  if (typeof localStorage === "undefined") return false;
  const stored = localStorage.getItem(KEY_PREFIX + key);
  if (stored) return false;
  localStorage.setItem(KEY_PREFIX + key, "1");
  fireConfetti(opts);
  return true;
}

// Clear a milestone marker (useful for testing / replays).
export function resetMilestone(key) {
  try { localStorage.removeItem(KEY_PREFIX + key); } catch { /* ignore */ }
}
